import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, Loader2, CheckCircle2, Undo2, Trash2, Tag, CalendarDays, Phone, MapPin,
  Camera, StickyNote, HardHat, X, Upload, Wrench, ChevronRight, Pencil, Check, Search, Clock,
} from "lucide-react";
import { api, API } from "../../lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
}
function fmtTs(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}

export default function InstallerContestazioni({ reloadKey, onOpenTask }) {
  const [data, setData] = useState(null); // { rate, counts, disputes }
  const [showResolved, setShowResolved] = useState(false);
  const [q, setQ] = useState("");
  const [lightbox, setLightbox] = useState(null); // { src, caption }
  const [assistOf, setAssistOf] = useState(null); // { loading, name } | { client, tasks }
  const [editingComment, setEditingComment] = useState(null); // { photoId, value }
  const token = localStorage.getItem("ah_token") || "";
  const photoSrc = (p) => `${API}${(p.url || "").replace("/api", "")}?token=${token}`;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/installers/admin/disputes", { params: { status: "all" } });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento contestazioni");
      setData({ rate: { year: 0, disputes: 0, deliveries: 0, rate_pct: 0 }, counts: { open: 0, resolved: 0 }, disputes: [] });
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  useEffect(() => {
    if (!lightbox && !assistOf) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (lightbox) setLightbox(null);
      else if (assistOf) setAssistOf(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, assistOf]);

  async function toggleResolved(d) {
    try {
      await api.patch(`/installers/admin/disputes/${d.task_id}`, { resolved: !d.resolved });
      toast.success(!d.resolved ? "Contestazione segnata come risolta" : "Contestazione riaperta");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento");
    }
  }

  async function dismiss(d) {
    if (!window.confirm(`Eliminare definitivamente la contestazione di "${d.client_name}"? La scheda sparirà dall'elenco (le foto restano nell'archivio cliente).`)) return;
    try {
      await api.delete(`/installers/admin/disputes/${d.task_id}`);
      toast.success("Contestazione eliminata");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    }
  }

  async function uploadPhotos(d, files) {
    if (!files || files.length === 0) return;
    let ok = 0;
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", "contestazione");
        fd.append("task_id", d.task_id);
        await api.post("/installers/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (err) {
        toast.error(err.response?.data?.detail || `Errore caricamento ${file.name}`);
      }
    }
    if (ok > 0) { toast.success(`${ok} foto aggiunte alla contestazione`); load(); }
  }

  async function deletePhoto(p) {
    if (!window.confirm("Eliminare questa foto della contestazione?")) return;
    try {
      await api.delete(`/installers/photos/${p.id}`);
      toast.success("Foto eliminata");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione foto");
    }
  }

  async function saveComment() {
    try {
      await api.patch(`/installers/photos/${editingComment.photoId}/comment`, { comment: editingComment.value });
      setEditingComment(null);
      toast.success("Commento aggiornato");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio commento");
    }
  }

  async function openAssistances(d) {
    setAssistOf({ loading: true, name: d.client_name });
    try {
      const { data } = await api.get(`/installers/admin/clients/${d.client_id}/history`);
      setAssistOf({ client: data.client, tasks: (data.tasks || []).filter((t) => t.task_type === "assistance") });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento assistenze");
      setAssistOf(null);
    }
  }

  // Raggruppa le contestazioni per cliente: una scheda per cliente, con le consegne suddivise dentro.
  const overdueDays = data?.overdue_threshold_days || 14;
  const groups = [];
  if (data) {
    const map = new Map();
    for (const d of data.disputes) {
      const key = d.client_id || `name:${(d.client_name || "").trim().toLowerCase()}`;
      let g = map.get(key);
      if (!g) {
        g = { key, client_name: d.client_name || d.title || "—", client_id: d.client_id, client_phone: "", client_address: "", assistance_count: 0, deliveries: [] };
        map.set(key, g);
        groups.push(g);
      }
      if (!g.client_phone && d.client_phone) g.client_phone = d.client_phone;
      if (!g.client_address && d.client_address) g.client_address = d.client_address;
      if ((d.assistance_count || 0) > g.assistance_count) g.assistance_count = d.assistance_count;
      g.deliveries.push(d);
    }
    for (const g of groups) {
      g.deliveries.sort((a, b) => (b.scheduled_date || "").localeCompare(a.scheduled_date || ""));
      g.open = g.deliveries.filter((d) => !d.resolved).length;
      g.resolved = g.open === 0; // cliente risolto solo quando tutte le sue consegne lo sono
      g.maxDaysOpen = Math.max(0, ...g.deliveries.filter((d) => !d.resolved).map((d) => d.days_open || 0));
      g.overdue = !g.resolved && g.maxDaysOpen >= overdueDays;
    }
    groups.sort((a, b) => (a.resolved === b.resolved ? 0 : a.resolved ? 1 : -1));
  }
  const clientResolvedCount = groups.filter((g) => g.resolved).length;
  const qlc = q.trim().toLowerCase();
  const matchGroup = (g) => {
    if (!qlc) return true;
    const hay = [
      g.client_name, g.client_phone, g.client_address,
      ...g.deliveries.flatMap((d) => [
        d.riferimento,
        d.scheduled_date ? fmtDate(d.scheduled_date) : "",
        ...(d.worker_notes || []).flatMap((n) => [n.text, n.user_name]),
      ]),
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(qlc);
  };
  const shownGroups = groups
    .filter((g) => (showResolved ? true : !g.resolved))
    .filter(matchGroup);

  return (
    <div className="space-y-4" data-testid="installers-contestazioni">
      {/* Totale contestazioni + tasso annuo */}
      {data && (() => {
        const totalRefs = (data.counts?.open || 0) + (data.counts?.resolved || 0);
        return (
        <div className="bg-white border border-ink/10 rounded-sm p-5 flex items-center gap-5 flex-wrap" data-testid="contestazioni-rate">
          <div className="w-12 h-12 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center shrink-0">
            <AlertTriangle size={22} className="text-rose-600" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate1">Contestazioni totali</div>
            <div className="font-display text-3xl text-ink leading-tight" data-testid="contestazioni-total">{totalRefs}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              <b className="text-rose-700">{data.counts?.open || 0}</b> in corso · <b className="text-emerald-700">{data.counts?.resolved || 0}</b> risolte
            </div>
          </div>
          <div className="text-sm text-slate-600 border-l border-ink/10 pl-5">
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate1">Tasso contestazione {data.rate.year}</div>
            <div className="font-display text-2xl text-ink leading-tight" data-testid="contestazioni-rate-pct">{data.rate.rate_pct}%</div>
            <div className="text-[11px] text-slate-500 mt-0.5"><b className="text-ink">{data.rate.disputes}</b> su <b className="text-ink">{data.rate.deliveries}</b> consegn{data.rate.deliveries === 1 ? "a" : "e"} dell&apos;anno</div>
          </div>
          <label className="ml-auto inline-flex items-center gap-2 cursor-pointer select-none" data-testid="contestazioni-toggle-resolved">
            <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} className="accent-emerald-600 w-4 h-4" />
            <span className="text-[11px] tracking-[0.14em] uppercase text-slate1">Mostra risolte ({clientResolvedCount})</span>
          </label>
        </div>
        );
      })()}

      {data && (() => {
        const overdueDisputes = data.disputes.filter((d) => !d.resolved && (d.days_open || 0) >= overdueDays).length;
        if (!overdueDisputes) return null;
        return (
        <div className="bg-rose-600 text-white rounded-sm px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="contestazioni-overdue-banner">
          <AlertTriangle size={18} className="shrink-0" />
          <span className="text-sm font-semibold">
            {overdueDisputes} contestazion{overdueDisputes === 1 ? "e" : "i"} apert{overdueDisputes === 1 ? "a" : "e"} da oltre {overdueDays} giorni
          </span>
          <span className="text-[12px] text-white/80">· da sollecitare o risolvere</span>
        </div>
        );
      })()}

      {data && (
        <div className="relative max-w-md" data-testid="contestazioni-search-wrap">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca contestazioni (cliente, indirizzo, telefono, rif., note…)"
            className="w-full border border-ink/20 pl-9 pr-9 py-2.5 rounded-sm bg-white text-sm"
            data-testid="contestazioni-search"
          />
          {q && (
            <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-ink" data-testid="contestazioni-search-clear"><X size={14} /></button>
          )}
        </div>
      )}

      {!data ? (
        <div className="flex justify-center py-14 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
      ) : shownGroups.length === 0 ? (
        <div className="bg-white border border-ink/10 rounded-sm p-10 text-center text-sm text-slate-400" data-testid="contestazioni-empty">
          <AlertTriangle size={26} className="mx-auto mb-2 text-slate-300" />
          {qlc ? "Nessuna contestazione trovata" : (showResolved ? "Nessuna contestazione" : "Nessuna contestazione in corso")}
          <div className="text-[11px] mt-1">Le schede compaiono in automatico quando un operaio carica foto in categoria «Contestazioni» o lascia un commento su una consegna.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {shownGroups.map((g) => (
            <div
              key={g.key}
              className={`bg-white border rounded-sm border-l-4 ${g.resolved ? "border-ink/10 border-l-emerald-500" : g.overdue ? "border-rose-300 border-l-rose-600 ring-1 ring-rose-200" : "border-ink/10 border-l-rose-500"}`}
              data-testid={`contestazione-client-${g.client_id || g.key}`}
            >
              {/* Intestazione cliente (una sola volta) */}
              <div className="p-4 border-b border-ink/10 flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-lg text-ink leading-tight" data-testid={`contestazione-client-name-${g.client_id || g.key}`}>{g.client_name}</span>
                    <span className={`text-[10px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 ${g.resolved ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-rose-50 text-rose-800 border-rose-200"}`} data-testid={`contestazione-client-status-${g.client_id || g.key}`}>
                      {g.resolved ? "Risolta" : "In corso"}
                    </span>
                    <span className="text-[11px] text-slate1">{g.deliveries.length} consegn{g.deliveries.length === 1 ? "a" : "e"}{g.open > 0 && !g.resolved ? ` · ${g.open} da risolvere` : ""}</span>
                    {g.overdue && (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 bg-rose-600 text-white border-rose-700" data-testid={`contestazione-overdue-${g.client_id || g.key}`}>
                        <Clock size={11} /> In ritardo · {g.maxDaysOpen} gg
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate1 mt-1.5 flex items-center gap-3 flex-wrap">
                    {g.client_phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {g.client_phone}</span>}
                    {g.client_address && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {g.client_address}</span>}
                  </div>
                </div>
                {g.assistance_count > 0 && g.client_id && (
                  <button
                    onClick={() => openAssistances({ client_id: g.client_id, client_name: g.client_name })}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-violet-50 border border-violet-300 text-violet-900 text-[10px] tracking-wider uppercase font-bold rounded-sm hover:bg-violet-100 transition-colors shrink-0"
                    data-testid={`contestazione-assist-${g.client_id || g.key}`}
                  >
                    <Wrench size={12} /> Assistenze ({g.assistance_count})
                  </button>
                )}
              </div>

              {/* Blocchi consegna */}
              <div className="divide-y divide-ink/10">
                {g.deliveries.map((d) => (
                  <div key={d.task_id} className={`p-4 ${d.resolved ? "bg-emerald-50/30" : ""}`} data-testid={`contestazione-${d.task_id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-[240px]">
                        <div className="flex items-center gap-2 flex-wrap text-sm">
                          <span className="inline-flex items-center gap-1 font-medium text-ink" data-testid={`contestazione-date-${d.task_id}`}><CalendarDays size={13} className="text-slate-400" /> Consegna {fmtDate(d.scheduled_date)}</span>
                          {d.riferimento && <span className="inline-flex items-center gap-1 text-ink" data-testid={`contestazione-rif-${d.task_id}`}><Tag size={12} className="text-slate-400" /> Rif. {d.riferimento}</span>}
                          <span className={`text-[9px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 ${d.resolved ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-rose-50 text-rose-800 border-rose-200"}`} data-testid={`contestazione-status-${d.task_id}`}>
                            {d.resolved ? "Risolta" : "In corso"}
                          </span>
                          {!d.resolved && d.days_open != null && (
                            <span className={`inline-flex items-center gap-1 text-[9px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 ${d.days_open >= overdueDays ? "bg-rose-600 text-white border-rose-700" : "bg-amber-50 text-amber-800 border-amber-200"}`} data-testid={`contestazione-age-${d.task_id}`}>
                              <Clock size={10} /> Aperta da {d.days_open} {d.days_open === 1 ? "giorno" : "giorni"}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {d.resolved ? (
                          <button onClick={() => toggleResolved(d)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-ink/15 text-slate-600 text-[10px] tracking-wider uppercase font-bold rounded-sm hover:border-ink/40" data-testid={`contestazione-reopen-${d.task_id}`}>
                            <Undo2 size={12} /> Riapri
                          </button>
                        ) : (
                          <button onClick={() => toggleResolved(d)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-[10px] tracking-wider uppercase font-bold rounded-sm hover:bg-emerald-700" data-testid={`contestazione-resolve-${d.task_id}`}>
                            <CheckCircle2 size={12} /> Segna come fatta
                          </button>
                        )}
                        <button onClick={() => dismiss(d)} className="p-2 text-rose-600 hover:text-rose-700 rounded-sm hover:bg-rose-50" title="Elimina definitivamente questa consegna" data-testid={`contestazione-del-${d.task_id}`}><Trash2 size={15} /></button>
                      </div>
                    </div>

                    {/* Commenti dell'installatore (note operaio) */}
                    {d.worker_notes.length > 0 && (
                      <div className="mt-3 space-y-1.5" data-testid={`contestazione-notes-${d.task_id}`}>
                        {d.worker_notes.map((n, i) => (
                          <div key={i} className="text-[12px] text-amber-900 inline-flex items-start gap-1.5 bg-amber-50 border border-amber-100 rounded-sm px-2.5 py-1.5 w-full">
                            <StickyNote size={12} className="shrink-0 mt-0.5 text-amber-600" />
                            <span><b>{n.user_name}</b>{n.text ? ` — ${n.text}` : ""} <span className="text-amber-700/70">· {fmtTs(n.created_at)}</span></span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Foto contestazione */}
                    <div className="mt-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="text-[11px] tracking-[0.14em] uppercase text-ink font-semibold inline-flex items-center gap-1.5">
                          <Camera size={13} /> Foto contestazione · {d.photo_count}
                        </div>
                        <label className="inline-flex items-center gap-1.5 text-[10px] tracking-wider uppercase font-bold text-slate-600 border border-dashed border-ink/25 rounded-sm px-2.5 py-1.5 cursor-pointer hover:bg-cream2 transition-colors" data-testid={`contestazione-upload-label-${d.task_id}`}>
                          <Upload size={12} /> Aggiungi foto
                          <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { uploadPhotos(d, e.target.files); e.target.value = ""; }} data-testid={`contestazione-upload-input-${d.task_id}`} />
                        </label>
                      </div>
                      {d.photos.length === 0 ? (
                        <div className="text-[12px] text-slate-400">Nessuna foto — usa «Aggiungi foto» per caricarne.</div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                          {d.photos.map((p) => (
                            <figure key={p.id} className="bg-cream2 border border-ink/10 rounded-sm overflow-hidden group" data-testid={`contestazione-photo-${p.id}`}>
                              <div className="relative aspect-[4/3] cursor-zoom-in" onClick={() => setLightbox({ src: photoSrc(p), caption: `${p.worker_name || ""} · ${fmtTs(p.uploaded_at)}` })}>
                                <img src={photoSrc(p)} alt="contestazione" loading="lazy" className="w-full h-full object-cover" />
                                <button onClick={(e) => { e.stopPropagation(); deletePhoto(p); }} className="absolute top-1 right-1 p-1 bg-white/85 text-rose-600 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity" title="Elimina foto" data-testid={`contestazione-photo-del-${p.id}`}><Trash2 size={13} /></button>
                              </div>
                              <figcaption className="p-2 text-[11px] text-slate-600 space-y-1">
                                <div className="flex items-center gap-1 truncate"><HardHat size={10} className="text-slate-400 shrink-0" /> <span className="truncate">{p.worker_name || "—"}{p.uploaded_by_admin ? " (admin)" : ""}</span></div>
                                {editingComment && editingComment.photoId === p.id ? (
                                  <div className="flex items-start gap-1">
                                    <textarea
                                      value={editingComment.value}
                                      onChange={(e) => setEditingComment({ ...editingComment, value: e.target.value })}
                                      rows={2}
                                      className="flex-1 border border-ink/20 rounded-sm px-1.5 py-1 text-[11px] bg-white"
                                      data-testid={`contestazione-comment-input-${p.id}`}
                                    />
                                    <button onClick={saveComment} className="p-1 text-emerald-700 hover:bg-emerald-50 rounded-sm" data-testid={`contestazione-comment-save-${p.id}`}><Check size={13} /></button>
                                    <button onClick={() => setEditingComment(null)} className="p-1 text-slate-400 hover:bg-ink/5 rounded-sm"><X size={13} /></button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setEditingComment({ photoId: p.id, value: p.comment || "" })}
                                    className="w-full text-left flex items-start gap-1 text-amber-800 hover:text-amber-900 group/comment"
                                    data-testid={`contestazione-comment-${p.id}`}
                                  >
                                    <StickyNote size={10} className="text-amber-500 shrink-0 mt-0.5" />
                                    <span className="line-clamp-2 flex-1">{p.comment || <span className="text-slate-400 italic">Aggiungi commento</span>}</span>
                                    <Pencil size={10} className="text-slate-300 shrink-0 mt-0.5 opacity-0 group-hover/comment:opacity-100" />
                                  </button>
                                )}
                              </figcaption>
                            </figure>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox foto */}
      {lightbox && (
        <div className="fixed inset-0 z-[9600] flex items-center justify-center bg-ink/85 p-4" data-testid="contestazioni-lightbox" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 p-2 text-cream hover:text-white" data-testid="contestazioni-lightbox-close"><X size={22} /></button>
          <div className="max-w-[92vw] max-h-[88vh] flex flex-col items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.src} alt="" className="max-w-full max-h-[82vh] object-contain rounded-sm" />
            {lightbox.caption && <div className="text-cream text-[12px]">{lightbox.caption}</div>}
          </div>
        </div>
      )}

      {/* Assistenze del cliente richiamate dalla scheda */}
      {assistOf && (
        <div className="fixed inset-0 z-[9500] flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4" data-testid="contestazioni-assist-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setAssistOf(null); }}>
          <div className="bg-cream w-[min(94vw,600px)] max-h-[86vh] flex flex-col border border-ink/15 shadow-2xl rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white px-6 py-4 border-b border-ink/10 flex items-center justify-between shrink-0">
              <div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-slate1">Assistenze del cliente</div>
                <div className="font-display text-lg text-ink leading-tight">{assistOf.loading ? assistOf.name : assistOf.client?.name}</div>
              </div>
              <button onClick={() => setAssistOf(null)} className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" data-testid="contestazioni-assist-close"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2">
              {assistOf.loading ? (
                <div className="flex justify-center py-10 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
              ) : assistOf.tasks.length === 0 ? (
                <div className="text-center py-10 text-sm text-slate-400" data-testid="contestazioni-assist-empty">Nessuna assistenza per questo cliente</div>
              ) : (
                assistOf.tasks.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => { if (onOpenTask) { setAssistOf(null); onOpenTask(t); } }}
                    className="w-full text-left bg-white border border-ink/10 border-l-4 border-l-violet-600 rounded-sm p-3 hover:border-ink/30 transition-[border-color] flex items-center gap-3"
                    data-testid={`contestazioni-assist-task-${t.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-ink">{t.title}</span>
                        <span className="text-[10px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 bg-violet-50 border-violet-300 text-violet-900">Assistenza</span>
                      </div>
                      <div className="mt-1 text-[12px] text-slate-600 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                        <span className="inline-flex items-center gap-1"><CalendarDays size={11} className="text-slate-400" /> {t.scheduled_date ? fmtDate(t.scheduled_date) : "Non pianificata"}</span>
                        {(t.workers || []).length > 0 && <span className="inline-flex items-center gap-1"><HardHat size={11} className="text-slate-400" /> {t.workers.map((w) => w.name).join(", ")}</span>}
                        {t.riferimento && <span className="inline-flex items-center gap-1"><Tag size={11} className="text-slate-400" /> {t.riferimento}</span>}
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-slate-300 shrink-0" />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
